import React, { useEffect, useState } from 'react';
import {
  Alert,
  Anchor,
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Grid,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ApiOutlined,
  ArrowLeftOutlined,
  BookOutlined,
  CloudDownloadOutlined,
  CopyOutlined,
  DatabaseOutlined,
  KeyOutlined,
  LaptopOutlined,
  LockOutlined,
  PythonOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import '../style/IntegrationWiki.css';

const { Paragraph, Text, Title } = Typography;

const TRIGGER_SCRIPT_URL = '/docs/examples/trigger_measurement.py';
const RETRIEVAL_SCRIPT_URL = '/docs/examples/retrieve_measurements.py';

const CONTROL_QUICK_START = `# Linux / macOS
python3 trigger_measurement.py --token 'oat_replace_with_your_control_token'

# Windows PowerShell
python .\\trigger_measurement.py --token 'oat_replace_with_your_control_token'`;

const CONTROL_TARGET_EXAMPLE = `python3 trigger_measurement.py \\
  --token 'oat_replace_with_your_control_token' \\
  --device-id 2 \\
  --sub-area-id 1 \\
  --external-ref packing-line-job-000184 \\
  --metadata-json '{"operator":"integration-a","batch":"B-1042"}'`;

const CONTROL_FLOW = `UDP 50505 discovery probe
  → GET  /api/remote/v1/identity
  → GET  /api/remote/v1/whoami          (control token)
  → GET  /api/remote/v1/status          (readiness)
  → POST /api/remote/v1/measurements/trigger
  → GET  <statusUrl from trigger response>
  → POST /api/remote/v1/session/release (optional handover)`;

const CONTROL_RELEASE_EXAMPLE = `python3 trigger_measurement.py \\
  --token 'oat_replace_with_your_control_token' \\
  --release-to-screen`;

const LIST_DEVICES_EXAMPLE = `curl "${ENDPOINTS.EXTERNAL_DEVICES}" \\
  -H "Authorization: Bearer oat_replace_with_your_retrieval_token"`;

const GET_MEASUREMENTS_EXAMPLE = `curl "${ENDPOINTS.EXTERNAL_DEVICE_MEASUREMENTS('<device-id>')}?from=2026-01-01T00%3A00%3A00Z&to=2026-01-31T23%3A59%3A59Z&limit=100" \\
  -H "Authorization: Bearer oat_replace_with_your_retrieval_token"`;

const CREATE_MISSION_DRAFT_EXAMPLE = `curl -X POST "${ENDPOINTS.EXTERNAL_MISSION_DRAFTS}" \\
  -H "Authorization: Bearer oat_replace_with_your_mission_token" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: job-4821" \\
  -d '{
    "externalReference": "job-4821",
    "name": "Inspection job 4821",
    "waypoints": [
      {"latitude": 51.501, "longitude": -0.142},
      {"latitude": 51.502, "longitude": -0.143, "altitude": 2.5}
    ]
  }'`;

const RETRIEVAL_PYTHON_EXAMPLE = `# List devices (development API is the default)
python3 retrieve_measurements.py \\
  --token 'oat_replace_with_your_retrieval_token' \\
  --list-devices

# Retrieve every page and write one combined JSON file
python3 retrieve_measurements.py \\
  --token 'oat_replace_with_your_retrieval_token' \\
  --device-id 8 \\
  --from '2026-08-01T00:00:00Z' \\
  --to '2026-08-12T23:59:59Z' \\
  --output measurements.json`;

const RETRIEVAL_DOWNLOAD_EXAMPLE = `python3 retrieve_measurements.py \\
  --token 'oat_replace_with_your_retrieval_token' \\
  --device-id 8 \\
  --from '2026-08-01T00:00:00Z' \\
  --to '2026-08-12T23:59:59Z' \\
  --output measurements.json \\
  --download-files ./measurement-files`;

const PAGINATION_EXAMPLE = `{
  "data": [
    {
      "id": 4821,
      "measurementCode": "LIGHTBOX_FULL_001",
      "deviceId": 2,
      "areaId": 41,
      "patternId": 17,
      "patternPointId": 93,
      "measuredAt": "2026-01-20T12:34:56.000Z",
      "values": [{ "valueType": "ndvi", "numValue": 0.71, "processingStage": "derived" }],
      "texts": [{ "textType": "trigger_metadata", "processingStage": "raw" }],
      "files": [{ "fileName": "capture.jpg", "processingStage": "raw", "downloadUrl": "https://..." }]
    }
  ],
  "pagination": {
    "limit": 100,
    "hasMore": true,
    "nextCursor": "eyJtZWFzdXJlZEF0IjoiLi4uIn0"
  }
}`;

const TRIAL_PLOT_AREA_EXAMPLE = String.raw`curl --fail-with-body -sS -X POST \
  "https://oscar-stack-dev.e-nano.io/external/v1/areas" \
  -H "Authorization: Bearer oat_REPLACE_WITH_MANAGEMENT_TOKEN" \
  -H "Content-Type: application/json" \
  -d '{
    "name": "API Test Trial Plot",
    "areaTypeId": 17,
    "spaceType": "outdoor",
    "geom": {
      "type": "Polygon",
      "coordinates": [[
        [-0.140, 51.500],
        [-0.139, 51.500],
        [-0.139, 51.501],
        [-0.140, 51.501],
        [-0.140, 51.500]
      ]]
    }
  }' \
  | python3 -m json.tool`;

const TRIAL_PLOT_PATTERN_EXAMPLE = String.raw`python3 - <<'PY' | curl --fail-with-body -sS -X POST \
  "https://oscar-stack-dev.e-nano.io/external/v1/patterns" \
  -H "Authorization: Bearer oat_REPLACE_WITH_MANAGEMENT_TOKEN" \
  -H "Content-Type: application/json" \
  --data-binary @- | python3 -m json.tool
import json

points = []
for row in range(7):
    columns = range(10) if row % 2 == 0 else range(9, -1, -1)
    for col in columns:
        points.append({
            "label": f"R{row + 1}C{col + 1}",
            "gpsLat": round(51.500 + 0.001 * (0.1 + 0.8 * row / 6), 7),
            "gpsLon": round(-0.140 + 0.001 * (0.1 + 0.8 * col / 9), 7)
        })

print(json.dumps({
    "name": "Trial Plot 81 - 7x10 GPS",
    "areaId": 81,
    "coordSystem": "gps",
    "patternType": "grid",
    "definition": {"rows": 7, "columns": 10, "ordering": "zigzag"},
    "points": points
}))
PY`;

const TRIAL_PLOT_READ_EXAMPLE = String.raw`# Replace <pattern-id> with data.id returned by pattern creation.
curl --fail-with-body -sS \
  "https://oscar-stack-dev.e-nano.io/external/v1/patterns/<pattern-id>/points?limit=100" \
  -H "Authorization: Bearer oat_REPLACE_WITH_MANAGEMENT_TOKEN" \
  | python3 -m json.tool

# All measurements assigned directly to the trial plot (first page).
curl --fail-with-body -sS \
  "https://oscar-stack-dev.e-nano.io/external/v1/areas/81/measurements?limit=100" \
  -H "Authorization: Bearer oat_REPLACE_WITH_MANAGEMENT_TOKEN" \
  | python3 -m json.tool

# One point in this area, with an optional date range.
# Replace <point-id> with the point's database id from the points response.
curl --fail-with-body -sS \
  "https://oscar-stack-dev.e-nano.io/external/v1/pattern-points/<point-id>/measurements?area_id=81&from=2026-10-01T00:00:00Z&to=2026-10-28T23:59:59.999Z" \
  -H "Authorization: Bearer oat_REPLACE_WITH_MANAGEMENT_TOKEN" \
  | python3 -m json.tool`;

interface CodeBlockProps {
  code: string;
  label?: string;
}

const copyText = async (value: string) => {
  try {
    await navigator.clipboard.writeText(value);
    message.success('Copied to clipboard');
  } catch {
    message.error('Copy failed — select and copy manually');
  }
};

const CodeBlock: React.FC<CodeBlockProps> = ({ code, label }) => (
  <div className="integration-wiki-code-wrap">
    <div className="integration-wiki-code-toolbar">
      <Text type="secondary">{label || 'Example'}</Text>
      <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => void copyText(code)}>
        Copy
      </Button>
    </div>
    <pre className="integration-wiki-code"><code>{code}</code></pre>
  </div>
);

const IntegrationWiki: React.FC = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const [scriptSource, setScriptSource] = useState('');
  const [scriptLoading, setScriptLoading] = useState(true);
  const [scriptError, setScriptError] = useState(false);
  const [retrievalScriptSource, setRetrievalScriptSource] = useState('');
  const [retrievalScriptLoading, setRetrievalScriptLoading] = useState(true);
  const [retrievalScriptError, setRetrievalScriptError] = useState(false);

  useEffect(() => {
    let active = true;
    fetch(TRIGGER_SCRIPT_URL, { cache: 'no-cache' })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load script (${response.status})`);
        return response.text();
      })
      .then((source) => {
        if (active) setScriptSource(source);
      })
      .catch(() => {
        if (active) setScriptError(true);
      })
      .finally(() => {
        if (active) setScriptLoading(false);
      });
    fetch(RETRIEVAL_SCRIPT_URL, { cache: 'no-cache' })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load retrieval script (${response.status})`);
        return response.text();
      })
      .then((source) => {
        if (active) setRetrievalScriptSource(source);
      })
      .catch(() => {
        if (active) setRetrievalScriptError(true);
      })
      .finally(() => {
        if (active) setRetrievalScriptLoading(false);
      });
    return () => { active = false; };
  }, []);

  const navigationItems = [
    { key: 'overview', href: '#overview', title: 'Overview' },
    { key: 'token-types', href: '#token-types', title: 'Choose the correct token' },
    {
      key: 'device-control',
      href: '#device-control',
      title: 'Control Lightboxes and devices',
      children: [
        { key: 'control-requirements', href: '#control-requirements', title: 'Requirements' },
        { key: 'trigger-script', href: '#trigger-script', title: 'Python example' },
        { key: 'control-handover', href: '#control-handover', title: 'Ownership and handover' },
      ],
    },
    {
      key: 'measurement-retrieval',
      href: '#measurement-retrieval',
      title: 'Retrieve measurements',
      children: [
        { key: 'list-devices', href: '#list-devices', title: 'List devices' },
        { key: 'area-point-measurements', href: '#area-point-measurements', title: 'By area or pattern point' },
        { key: 'date-range', href: '#date-range', title: 'Date-range query' },
        { key: 'retrieval-python', href: '#retrieval-python', title: 'Python example' },
        { key: 'response-contract', href: '#response-contract', title: 'Response and pagination' },
      ],
    },
    { key: 'areas-patterns', href: '#areas-patterns', title: 'Create and archive areas and patterns', children: [
      { key: 'trial-plot-example', href: '#trial-plot-example', title: 'Worked example: Trial plots, 7×10 GPS grid' },
    ] },
    { key: 'mission-drafts', href: '#mission-drafts', title: 'Submit mission drafts' },
    { key: 'security', href: '#security', title: 'Security and token lifecycle' },
    { key: 'troubleshooting', href: '#troubleshooting', title: 'Troubleshooting' },
  ];

  return (
    <div className="integration-wiki-page">
      <header className="integration-wiki-hero" id="overview">
        <Space className="integration-wiki-hero-actions" wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/configuration?tab=api-tokens')}>
            Back to API Tokens
          </Button>
          <Button icon={<SettingOutlined />} onClick={() => navigate('/configuration')}>
            Configuration
          </Button>
        </Space>
        <div className="integration-wiki-kicker"><BookOutlined /> OSCAR Integration Wiki</div>
        <Title level={1}>Control devices, manage areas and patterns, and retrieve data</Title>
        <Paragraph className="integration-wiki-lead">
          This guide covers the supported external integration flows: controlling a Lightbox,
          submitting waypoint mission drafts, creating areas and patterns, and reading OSCAR measurements. Each flow uses a
          different token so its access remains independently auditable and revocable.
        </Paragraph>
        <Space wrap>
          <Tag color="purple" icon={<LaptopOutlined />}>Local device control</Tag>
          <Tag color="blue" icon={<DatabaseOutlined />}>Read-only cloud retrieval</Tag>
          <Tag color="orange" icon={<ApiOutlined />}>Mission draft submission</Tag>
          <Tag color="green" icon={<SafetyCertificateOutlined />}>Company scoped</Tag>
        </Space>
      </header>

      <Row gutter={[24, 24]} align="top">
        {screens.lg && (
          <Col lg={6} xl={5}>
            <Card size="small" className="integration-wiki-nav-card">
              <Text strong>On this page</Text>
              <Anchor affix={false} items={navigationItems} offsetTop={84} />
            </Card>
          </Col>
        )}

        <Col xs={24} lg={18} xl={19}>
          {!screens.lg && (
            <Collapse
              className="integration-wiki-mobile-nav"
              items={[{
                key: 'contents',
                label: 'On this page',
                children: <Anchor affix={false} items={navigationItems} offsetTop={72} />,
              }]}
            />
          )}

          <section id="token-types" className="integration-wiki-section">
            <Title level={2}><KeyOutlined /> Choose the correct token</Title>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Card title="Area and Pattern Management" className="integration-wiki-purpose-card">
                  <Paragraph>Create areas, subareas, and ordered pattern points, archive or restore them,
                    and retrieve measurements with their area and pattern IDs.</Paragraph>
                  <Alert type="info" showIcon message="Company administrators can write. Archived records are hidden and excluded from Lightbox downloads." />
                  <a href="#areas-patterns">Open area and pattern API guide</a>
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card title="Lightbox / Device Control" className="integration-wiki-purpose-card control">
                  <Paragraph>
                    Use this token with the Lightbox local API to discover a box, inspect readiness,
                    trigger a capture, follow processing/upload status, and explicitly return control
                    to the touchscreen.
                  </Paragraph>
                  <Text strong>Permissions:</Text>
                  <div className="integration-wiki-scope-list">
                    <Text code>measurement:trigger</Text>
                    <Text code>measurement:read</Text>
                    <Text code>device:status</Text>
                  </div>
                  <Alert type="warning" showIcon message="This token can control equipment. Do not share it with data consumers." />
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card title="External Measurement Access" className="integration-wiki-purpose-card retrieval">
                  <Paragraph>
                    Use this token with OSCAR's versioned cloud API to list accessible company devices
                    and retrieve all linked measurement values, texts, and ready files in a date range.
                  </Paragraph>
                  <Text strong>Permissions:</Text>
                  <div className="integration-wiki-scope-list">
                    <Text code>external:devices:list</Text>
                    <Text code>external:measurements:read</Text>
                  </div>
                  <Alert type="info" showIcon message="This token is read-only and cannot trigger or upload measurements." />
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card title="External Mission Drafts" className="integration-wiki-purpose-card">
                  <Paragraph>
                    Submit ordered GPS waypoints for the robot linked to the token. OSCAR stores an inert draft
                    until an operator assigns an area and adds START and END points.
                  </Paragraph>
                  <Text strong>Permissions:</Text>
                  <div className="integration-wiki-scope-list">
                    <Text code>external:devices:list</Text>
                    <Text code>external:missions:create</Text>
                  </div>
                  <Alert type="info" showIcon message="This token can create drafts, but cannot schedule or run them." />
                </Card>
              </Col>
            </Row>
            <Paragraph className="integration-wiki-action-row">
              <Button type="primary" icon={<KeyOutlined />} onClick={() => navigate('/configuration?tab=api-tokens')}>
                Create or manage API tokens
              </Button>
            </Paragraph>
          </section>

          <section id="device-control" className="integration-wiki-section">
            <Title level={2}><LaptopOutlined /> Control Lightboxes and devices</Title>
            <Paragraph>
              Device control happens directly between the integration computer and the Lightbox over
              WiFi or Ethernet. The normal client only needs a device-control <Text code>oat_</Text>{' '}
              token; it can discover the authorized Lightbox without knowing its IP address.
            </Paragraph>

            <div id="control-requirements" className="integration-wiki-subsection">
              <Title level={3}>Requirements and network flow</Title>
              <ol className="integration-wiki-steps">
                <li>Create a <Text strong>Lightbox / Device Control</Text> token in Configuration.</li>
                <li>Place the client computer and Lightbox on the same reachable LAN or subnet.</li>
                <li>Allow TCP <Text code>5000</Text>. UDP <Text code>50505</Text> enables fast discovery.</li>
                <li>Enable <Text code>LIGHTBOX_REMOTE_API_ENABLED=1</Text> on the Lightbox.</li>
                <li>Keep the rocker switch on and wait until the camera reports ready.</li>
                <li>Use the token once while the box can reach OSCAR so it can validate and cache it.</li>
              </ol>
              <Alert
                type="info"
                showIcon
                message="After its first successful online validation, the token can be used over Ethernet while the Lightbox is offline."
              />
              <CodeBlock code={CONTROL_FLOW} label="Supported control sequence" />
            </div>

            <div id="trigger-script" className="integration-wiki-subsection">
              <Title level={3}><PythonOutlined /> Python trigger example</Title>
              <Paragraph>
                The supported example uses only Python's standard library. It discovers Lightboxes,
                verifies which box accepts the token, checks camera readiness, sends an idempotent
                trigger, and waits for cloud synchronization by default.
              </Paragraph>
              <Space wrap className="integration-wiki-download-actions">
                <Button type="primary" icon={<CloudDownloadOutlined />} href={TRIGGER_SCRIPT_URL} download="trigger_measurement.py">
                  Download trigger_measurement.py
                </Button>
                <Button
                  icon={<CopyOutlined />}
                  disabled={!scriptSource}
                  onClick={() => void copyText(scriptSource)}
                >
                  Copy full script
                </Button>
              </Space>
              <CodeBlock code={CONTROL_QUICK_START} label="Quick start" />
              <CodeBlock code={CONTROL_TARGET_EXAMPLE} label="Pinned device, context and idempotency" />
              <Alert
                type="warning"
                showIcon
                message="Retry an uncertain request with the same externalRef. A new externalRef intentionally creates another capture."
              />
              <Collapse
                className="integration-wiki-source-collapse"
                items={[{
                  key: 'source',
                  label: `View complete trigger_measurement.py${scriptSource ? ` (${scriptSource.replace(/\n$/, '').split('\n').length} lines)` : ''}`,
                  children: scriptLoading ? (
                    <div className="integration-wiki-source-loading"><Spin /> Loading source…</div>
                  ) : scriptError ? (
                    <Alert type="error" showIcon message="The script source could not be loaded. Use the download link or contact an OSCAR administrator." />
                  ) : (
                    <pre className="integration-wiki-source"><code>{scriptSource}</code></pre>
                  ),
                }]}
              />
            </div>

            <div id="control-handover" className="integration-wiki-subsection">
              <Title level={3}>Remote ownership and touchscreen handover</Title>
              <Paragraph>
                A successful remote trigger makes the API-token user the active Lightbox owner. The
                previous touchscreen credentials are removed, and queued uploads continue under the
                remote user's company. The box remains in remote mode after the script exits.
              </Paragraph>
              <CodeBlock code={CONTROL_RELEASE_EXAMPLE} label="Explicitly return control to the touchscreen" />
              <Paragraph>
                A successful touchscreen login is the other supported handover path. Do not release
                control while an external controller still owns the workflow.
              </Paragraph>
            </div>
          </section>

          <section id="measurement-retrieval" className="integration-wiki-section">
            <Title level={2}><DatabaseOutlined /> Retrieve measurements from OSCAR</Title>
            <Paragraph>
              Cloud retrieval uses an <Text strong>External Measurement Access</Text> token and versioned, read-only endpoints. Send the token as{' '}
              <Text code>Authorization: Bearer oat_…</Text> on every request.
            </Paragraph>

            <div id="list-devices" className="integration-wiki-subsection">
              <Title level={3}>1. List accessible devices</Title>
              <CodeBlock code={LIST_DEVICES_EXAMPLE} label="GET /external/v1/devices" />
              <Paragraph>
                The response contains safe identity and status fields only. Company-wide tokens see
                assigned company devices; device-restricted tokens see only their pinned device.
                Device API keys and internal configuration are never returned.
              </Paragraph>
            </div>

            <div id="area-point-measurements" className="integration-wiki-subsection">
              <Title level={3}>Retrieve by area or pattern point</Title>
              <CodeBlock label="Area and point retrieval (dates are optional)" code={`GET /external/v1/areas/81/measurements
GET /external/v1/areas/81/measurements?from=2026-10-01T00:00:00Z&to=2026-10-28T23:59:59Z
GET /external/v1/pattern-points/<point-id>/measurements
GET /external/v1/pattern-points/<point-id>/measurements?area_id=81&from=2026-10-01T00:00:00Z
GET /external/v1/areas/81/measurements?pattern_point_id=<point-id>&limit=100`} />
              <Paragraph>
                With no dates these endpoints return all history, one page at a time. Supply from, to,
                or both as ISO timestamps with a timezone; bounds are inclusive and use measuredAt,
                falling back to createdAt. Area queries match the exact area ID and do not include
                subareas automatically. Use the point’s database ID, not its pointIndex or label.
                For reusable points, area_id restricts the results to one area. Area requests can
                combine pattern_point_id with the area ID as well.
              </Paragraph>
              <Paragraph>
                These routes use external:measurements:read, so existing retrieval and management
                tokens work. They enforce company ownership and any device restriction, include
                historical data from archived areas and patterns, and return the same values,
                texts, files, and ID references as device retrieval. Unlike the device route,
                optional area/point ranges have no 31-day cap. Missing date bounds are returned
                as null in range. Follow pagination.nextCursor with the same filters until
                pagination.hasMore is false; limit defaults to 100 and has a maximum of 500.
              </Paragraph>
            </div>

            <div id="date-range" className="integration-wiki-subsection">
              <Title level={3}>2. Retrieve measurements in a date range</Title>
              <CodeBlock code={GET_MEASUREMENTS_EXAMPLE} label="GET /external/v1/devices/{deviceId}/measurements" />
              <ul>
                <li><Text code>from</Text> and <Text code>to</Text> are required ISO-8601 timestamps with an explicit timezone.</li>
                <li>The range is inclusive and defaults to a maximum of 31 days.</li>
                <li><Text code>limit</Text> defaults to 100 and cannot exceed 500.</li>
                <li>Ordering uses <Text code>measuredAt</Text>, falling back to <Text code>createdAt</Text>.</li>
              </ul>
            </div>

            <div id="retrieval-python" className="integration-wiki-subsection">
              <Title level={3}><PythonOutlined /> Tested Python retrieval example</Title>
              <Paragraph>
                The standard-library client has been validated against the OSCAR development API. It
                lists accessible devices, follows every opaque pagination cursor, writes a combined
                JSON result, and can optionally download every ready file without sending the OSCAR
                bearer token to signed storage URLs.
              </Paragraph>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="The script defaults to https://oscar-stack-dev.e-nano.io and accepts credentials through --token only."
              />
              <Space wrap className="integration-wiki-download-actions">
                <Button type="primary" icon={<CloudDownloadOutlined />} href={RETRIEVAL_SCRIPT_URL} download="retrieve_measurements.py">
                  Download retrieve_measurements.py
                </Button>
                <Button
                  icon={<CopyOutlined />}
                  disabled={!retrievalScriptSource}
                  onClick={() => void copyText(retrievalScriptSource)}
                >
                  Copy full script
                </Button>
              </Space>
              <CodeBlock code={RETRIEVAL_PYTHON_EXAMPLE} label="List devices and retrieve measurements" />
              <CodeBlock code={RETRIEVAL_DOWNLOAD_EXAMPLE} label="Retrieve JSON and download ready files" />
              <Collapse
                className="integration-wiki-source-collapse"
                items={[{
                  key: 'retrieval-source',
                  label: `View complete retrieve_measurements.py${retrievalScriptSource ? ` (${retrievalScriptSource.replace(/\n$/, '').split('\n').length} lines)` : ''}`,
                  children: retrievalScriptLoading ? (
                    <div className="integration-wiki-source-loading"><Spin /> Loading source…</div>
                  ) : retrievalScriptError ? (
                    <Alert type="error" showIcon message="The retrieval script could not be loaded. Use the download link or contact an OSCAR administrator." />
                  ) : (
                    <pre className="integration-wiki-source"><code>{retrievalScriptSource}</code></pre>
                  ),
                }]}
              />
            </div>

            <div id="response-contract" className="integration-wiki-subsection">
              <Title level={3}>3. Consume all linked data and follow pagination</Title>
              <Paragraph>
                Each result includes every linked measurement value, text, and ready file across the
                <Text code>raw</Text>, <Text code>processed</Text>, and <Text code>derived</Text> stages.
                File records include short-lived HTTPS download URLs when storage signing is available.
              </Paragraph>
              <Paragraph>
                <Text code>areaId</Text> identifies the assigned area or subarea (subareas are areas with a
                <Text code>parentAreaId</Text>). <Text code>patternId</Text> and <Text code>patternPointId</Text>
                identify the assigned pattern and its point. Unassigned references are null. These IDs remain
                valid after archiving; archiving preserves historical measurements.
              </Paragraph>
              <CodeBlock code={PAGINATION_EXAMPLE} label="Abbreviated response" />
              <Paragraph>
                When <Text code>pagination.hasMore</Text> is true, repeat the same request with{' '}
                <Text code>&cursor=&lt;nextCursor&gt;</Text>. Treat the cursor as opaque and continue until
                <Text code>hasMore</Text> is false.
              </Paragraph>
            </div>
          </section>

          <section id="areas-patterns" className="integration-wiki-section">
            <Title level={2}><ApiOutlined /> Create and archive areas and patterns</Title>
            <Paragraph>
              In Settings → API Tokens, create an <Text strong>Area and Pattern Management</Text> token.
              Use <Text code>Authorization: Bearer oat_…</Text> with the OSCAR cloud API. Writes require
              the token owner to remain a company administrator. This purpose includes measurement retrieval;
              existing retrieval tokens remain read-only. All areas and newly created patterns belong to the
              token’s company. An optional device restriction affects retrieval, not company-wide structure access.
            </Paragraph>
            <ul>
              <li><Text code>GET /external/v1/areas</Text> lists areas; filter children with <Text code>parent_area_id</Text>.</li>
              <li><Text code>GET /external/v1/area-types</Text> lists available area type IDs.</li>
              <li><Text code>POST /external/v1/areas</Text> creates an area. Supply <Text code>parentAreaId</Text> to create a subarea; otherwise the company root is used.</li>
              <li><Text code>POST /external/v1/patterns</Text> creates a pattern and its points together. Supply exactly one of <Text code>areaId</Text> or <Text code>areaTypeId</Text>.</li>
              <li><Text code>GET /external/v1/patterns</Text> accepts <Text code>area_id</Text> or <Text code>area_type_id</Text>; <Text code>GET /external/v1/patterns/&lt;id&gt;/points</Text> returns persisted point IDs.</li>
              <li>List endpoints accept <Text code>limit</Text> (1–500, default 100) and <Text code>offset</Text>. Advance offset until total is reached.</li>
            </ul>
            <CodeBlock label="Create an area, subarea, and GPS pattern (replace IDs with returned data.id)" code={`POST /external/v1/areas
{"name":"Field A","geom":{"type":"Polygon","coordinates":[[[-0.14,51.5],[-0.13,51.5],[-0.13,51.51],[-0.14,51.5]]]}}

POST /external/v1/areas
{"name":"Plot 1","parentAreaId":41}

POST /external/v1/patterns
{"name":"Inspection points","areaId":42,"coordSystem":"gps","patternType":"custom","points":[{"gpsLat":51.502,"gpsLon":-0.135,"label":"P1"},{"gpsLat":51.503,"gpsLon":-0.134,"label":"P2"}]}

GET /external/v1/patterns/17/points`} />
            <Paragraph>
              Area geometry is optional GeoJSON Point or closed Polygon, using longitude then latitude.
              Optional fields are code, areaTypeId, spaceType (outdoor, indoor, mixed), slamMapId, and configurations.slamPlacement (lat, lng, bearingDegrees). Indoor geometry may use local metres; its saved or inherited lock in space is checked against the company boundary.
              Patterns require 1–10000 ordered points. GPS points use gpsLat/gpsLon and optional gpsAlt;
              Area-specific local coordinates are reserved for indoor maps; mixed points require both.
              Reusable normalized patterns require areaTypeId, coordSystem=local, and xLocal/yLocal between
              0 and 1 on a standard four-corner template. Their definition uses boxWidth=1 and boxHeight=1.
              Types configured for GPS-only points, missing templates, and non-standard shapes require
              areaId and GPS points instead. An uploaded image alone does not make a normalized template.
              Point indexes are assigned from zero.
              The API stores supplied points; generate grids or routes in your integration before submitting.
              Patterns may also include an object in definition. Creation returns HTTP 201 and data.id.
            </Paragraph>
            <Paragraph>
              Report heatmaps choose one coordinate frame per area over the selected date range.
              GPS-only and mixed GPS/normalized history use a geographic map; normalized points are
              projected with the existing area conversion helper when a valid four-corner boundary
              is available. Normalized-only history retains its template view, including older
              patterns whose area type is now GPS-only. Animation frames keep the same coordinate
              frame across the range. Samples without a reliable position are counted in a visible
              notice; their measurement values remain in numeric summaries. Report conversion never
              rewrites stored point coordinates.
            </Paragraph>
            <div id="trial-plot-example" className="integration-wiki-subsection">
              <Title level={3}>Worked example: Trial plots with a non-reusable 7×10 GPS grid</Title>
              <Paragraph>
                This is the development example used to create API Test Trial Plot and a 70-point
                GPS pattern. Commands use an inline token placeholder and no environment variables.
                Replace the placeholder with your Area and Pattern Management token. Trial plots had
                areaTypeId 17 in this environment; confirm IDs with GET /external/v1/area-types.
                The boundary below is an example in longitude/latitude order. Its repeated final
                coordinate closes the polygon: it still has four corners.
              </Paragraph>
              <Title level={4}>1. Create the Trial plots area</Title>
              <CodeBlock code={TRIAL_PLOT_AREA_EXAMPLE} label="Create the four-corner outdoor Trial plots area" />
              <Paragraph>
                The development run returned data.id = 81, areaTypeId = 17, and archivedAt = null.
                With parentAreaId omitted, the API placed it under the token company’s root area.
                Use your own returned area ID in every subsequent command. Creation requests are
                not idempotent; keep the returned IDs before continuing.
              </Paragraph>
              <Title level={4}>2. Create 7 rows × 10 columns of fixed GPS points</Title>
              <Paragraph>
                The script generates exactly 70 points, inset 10% from each edge, in a zigzag order.
                Labels run from R1C1 through R7C10. This formula is specific to the rectangular example
                above; use a boundary-aware grid generator for a rotated or irregular area.
                If your area ID differs, replace 81 in the request and pattern name.
              </Paragraph>
              <CodeBlock code={TRIAL_PLOT_PATTERN_EXAMPLE} label="Generate and create the non-reusable 7×10 GPS pattern" />
              <Paragraph>
                The area keeps its Trial plots classification through areaTypeId 17. The pattern
                uses areaId 81 and coordSystem gps, so its points belong to this particular area.
                A pattern-level areaTypeId would instead create a reusable type-level pattern and
                is deliberately omitted. Four corners alone do not make this pattern reusable.
                Only gpsLat/gpsLon are stored; there are no normalized xLocal/yLocal coordinates.
                Keep the pattern’s returned data.id and check numPoints is 70.
              </Paragraph>
              <Title level={4}>3. Read the point IDs and retrieve linked measurements</Title>
              <CodeBlock code={TRIAL_PLOT_READ_EXAMPLE} label="Read pattern points; retrieve by area or point, optionally filtered by date" />
              <Paragraph>
                Point IDs are database IDs, distinct from labels and sequence indexes. Subsequent
                captures must include areaId 81, the returned patternId, and the chosen patternPointId.
                Use a separate Device Control token for Lightbox captures. Area and pattern creation
                alone do not produce measurements, so retrieval can return an empty data array until
                captures are uploaded. The area/point retrieval routes require the updated backend.
                Omit from and to to retrieve all history; follow pagination.nextCursor on the same
                endpoint with unchanged filters until pagination.hasMore is false.
              </Paragraph>
            </div>

            <CodeBlock label="Archive or restore" code={`POST /external/v1/areas/42/archive
{"archived":true}

POST /external/v1/patterns/17/archive
{"archived":true}

# Restore the specified record only:
POST /external/v1/areas/42/archive
{"archived":false}`} />
            <Paragraph>
              Archive controls are also available in the Areas and Patterns screens. Archived records are
              hidden by default and excluded from Lightbox catalog downloads, points, and preview assets.
              Archiving an area also archives its descendants and area-specific patterns; the company root
              cannot be archived. Historical measurements and their IDs are retained. Use Show archived in
              OSCAR, or include_archived=true (all) / archived=true (archived only) on API lists, to find and
              restore records. Restore parents before children, then restore patterns separately.
              Reports retain archived areas, patterns, and their measurements. Enable Show archived areas
              in the report browser to find archived areas; existing reports keep rendering their historical data.
              Lightboxes receive archive changes on their next successful catalog sync; offline caches cannot
              know about cloud changes until then.
            </Paragraph>
            <Alert type="info" showIcon message="Company operating boundaries" description="When a Back Office operating boundary is enabled, new areas, area-specific pattern points, restored resources, and submitted measurement locations must fit inside it. The whole area or route is checked, including holes and gaps. A token cannot change or bypass this boundary. Historical reports remain available. Outdoor areas use their geographic footprint. Indoor and mixed areas use their saved lock in space (SLAM placement or map geo-anchor, including inherited placement). Local map coordinates are not treated as GPS. Device-side Lightbox capture and offline enforcement still require an update." />
            <Paragraph>
              Boundary failures return 403 with a descriptive message, including missing geographic location,
              an outside location, or a missing required company boundary. Ask your administrator to review
              the approved geography. Selecting an active area within the current company boundary permits
              measurement using its centre without GPS. Without a predefined area, GPS is required and
              must remain within the company boundary.
              The example coordinates above must be replaced with coordinates inside your approved boundary.
            </Paragraph>
            <Paragraph>
              With a separate Device Control token, pass the returned areaId, patternId, and patternPointId
              to the Lightbox measurement trigger. The cloud measurement response returns those same IDs.
              subAreaId on the Lightbox trigger is a numeric measurement code segment, not an OSCAR area ID.
              Use areaId for the actual OSCAR subarea link.
            </Paragraph>
            <Paragraph>
              Management scopes: external:areas:read, external:areas:write, external:patterns:read,
              external:patterns:write, external:devices:list, external:measurements:read.
              Invalid input returns 400, missing scopes or administrator privileges 403, inaccessible records
              404, and archived-parent or root-area conflicts 409. Creation is not idempotent; do not blindly retry POST requests.
            </Paragraph>
          </section>

          <section id="mission-drafts" className="integration-wiki-section">
            <Title level={2}><ApiOutlined /> Submit waypoint mission drafts</Title>
            <Paragraph>
              Use an <Text strong>External Mission Drafts</Text> token to create one mission draft per
              external job. Waypoints are stored directly on the mission and are not added to the
              reusable pattern library.
            </Paragraph>
            <CodeBlock code={CREATE_MISSION_DRAFT_EXAMPLE} label="POST /external/v1/mission-drafts" />
            <ul>
              <li><Text code>externalReference</Text> is required and idempotent within the company.</li>
              <li>The mission token must be linked to a company robot; requests do not include <Text code>deviceId</Text>.</li>
              <li><Text code>waypoints</Text> must contain 1–1000 valid GPS latitude/longitude pairs.</li>
              <li>The endpoint creates a <Text code>draft</Text> using <Text code>route_following</Text>, with no area or pattern.</li>
              <li>Each imported waypoint uses the default command of every active module on the linked robot.</li>
              <li>An operator must select an area and add exactly one START and END before scheduling or execution.</li>
            </ul>
            <Alert
              type="warning"
              showIcon
              message="Drafts are deliberately inert"
              description="OSCAR validates readiness again on scheduling, operation attachment, manual start, and final dispatch."
            />
          </section>

          <section id="security" className="integration-wiki-section">
            <Title level={2}><LockOutlined /> Security and token lifecycle</Title>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Card size="small" title="Recommended">
                  <ul>
                    <li>Issue one retrieval token per partner and environment.</li>
                    <li>Use an expiry and a single-device restriction when practical.</li>
                    <li>Store raw tokens in a secret manager.</li>
                    <li>Rotate immediately after suspected exposure.</li>
                    <li>Use HTTPS for every OSCAR cloud request.</li>
                  </ul>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card size="small" title="Never do this">
                  <ul>
                    <li>Do not reuse a control token for data retrieval.</li>
                    <li>Do not commit tokens to source control.</li>
                    <li>Do not log full bearer tokens.</li>
                    <li>Do not share a partner token between companies.</li>
                    <li>Do not rely on a temporary file URL after it expires.</li>
                  </ul>
                </Card>
              </Col>
            </Row>
            <Alert
              type="success"
              showIcon
              message="Revoked, expired, or membership-orphaned tokens stop working. Rotation revokes the previous raw token."
            />
          </section>

          <section id="troubleshooting" className="integration-wiki-section">
            <Title level={2}><ApiOutlined /> Troubleshooting</Title>
            <Collapse
              items={[
                {
                  key: 'no-lightbox',
                  label: 'No Lightbox is discovered',
                  children: <Paragraph>Confirm TCP 5000, UDP 50505, subnet reachability, the rocker switch, and <Text code>LIGHTBOX_REMOTE_API_ENABLED=1</Text>. The Python client falls back to a bounded local subnet scan when broadcast discovery is suppressed.</Paragraph>,
                },
                {
                  key: 'validation-internet',
                  label: 'Token validation requires internet',
                  children: <Paragraph>Connect the Lightbox to OSCAR, use the device-control token once, and then retry the Ethernet workflow. First use must populate the device's validated token cache.</Paragraph>,
                },
                {
                  key: 'wrong-scope',
                  label: '401 or 403 from an API endpoint',
                  children: <Paragraph>Confirm the token purpose. Control, retrieval, and mission-draft tokens have separate permissions. Also check expiry, revocation, company membership, and optional device restriction.</Paragraph>,
                },
                {
                  key: 'bad-date',
                  label: 'Date range request returns 400',
                  children: <Paragraph>Provide timezone-aware ISO timestamps, keep <Text code>from</Text> before <Text code>to</Text>, use a range no longer than 31 days, and keep the page limit at 500 or below.</Paragraph>,
                },
                {
                  key: 'missing-files',
                  label: 'A file is missing or its URL has expired',
                  children: <Paragraph>Only files in the ready state are returned. Repeat the measurement query to obtain fresh signed URLs. The API can only return artifacts that were successfully uploaded to OSCAR.</Paragraph>,
                },
              ]}
            />
          </section>

          <Divider />
          <footer className="integration-wiki-footer">
            <Space wrap>
              <Button type="primary" icon={<KeyOutlined />} onClick={() => navigate('/configuration?tab=api-tokens')}>
                Manage API tokens
              </Button>
              <Button icon={<CloudDownloadOutlined />} href={TRIGGER_SCRIPT_URL} download="trigger_measurement.py">
                Download trigger example
              </Button>
              <Button icon={<CloudDownloadOutlined />} href={RETRIEVAL_SCRIPT_URL} download="retrieve_measurements.py">
                Download retrieval example
              </Button>
            </Space>
          </footer>
        </Col>
      </Row>
    </div>
  );
};

export default IntegrationWiki;
